import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ENVIRONMENT_INITIALIZER,
  EnvironmentProviders,
  inject,
  InjectionToken,
  makeEnvironmentProviders,
  Optional,
  Provider,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  Configuration,
  EventMessage,
  EventType,
  LogLevel,
  RedirectRequest,
} from '@azure/msal-browser';
import {
  BannerV3Service,
  failedRequestInterceptorFn,
  GeaUiLibConfiguration,
  HeaderConfig,
  LoginService,
  MonitoringService,
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
  OrtusLightPreset,
  ResourcesService,
  THEME_PRESET,
  THEME_PRESET_INITIALIZER,
  versionInterceptorFn,
} from '@gea/digital-ui-lib';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { filter } from 'rxjs';
import { customMsalInterceptorFn } from '../interceptor/msal.interceptor.fn';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export const msalConfig: Configuration = {
  auth: {
    clientId: '', // This is the ONLY mandatory field that you need to supply.
    authority: '', // Defaults to "https://login.microsoftonline.com/common"
    knownAuthorities: [], // Mark your B2C tenant's domain as trusted.
    redirectUri: '', // Points to window.location.origin. You must register this URI on Azure portal/App Registration.
    postLogoutRedirectUri: '', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: BrowserCacheLocation.SessionStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: isIE, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    tokenRenewalOffsetSeconds: 180,
    loggerOptions: {
      // leaving values in to see what is being passed
      // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
      loggerCallback(_logLevel: LogLevel, _message: string) {
        // uncomment for debugging MSAL
        // console.log(_message);
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Provides the GEA Digital UI Library configuration as standalone environment providers.
 * This replaces `HeaderModule.forRoot(config)` for standalone/bootstrapApplication-based apps.
 *
 * @param config - Either a HeaderConfig object or an InjectionToken<HeaderConfig>
 * @returns EnvironmentProviders to be passed to `providers` in `bootstrapApplication` or a route config
 *
 * @example
 * ```ts
 * bootstrapApplication(AppComponent, {
 *   providers: [
 *     provideGea(myHeaderConfig),
 *     // or provideGea(MY_HEADER_CONFIG_TOKEN),
 *   ],
 * });
 * ```
 */
export function provideGeaCustom(
  config: InjectionToken<HeaderConfig> | HeaderConfig,
): EnvironmentProviders {
  const providers: (Provider | EnvironmentProviders)[] = [
    TranslatePipe,
    ConfirmationService,
    MessageService,
    BannerV3Service,
    provideHttpClient(
      withInterceptors([customMsalInterceptorFn, versionInterceptorFn, failedRequestInterceptorFn]),
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        // this is how primeng defines the preset
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        preset: OrtusLightPreset,
        options: {
          darkModeSelector: 'none',
        },
      },
    }),
    {
      provide: THEME_PRESET,
      // this is how primeng defines the preset
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      useValue: OrtusLightPreset,
    },
    THEME_PRESET_INITIALIZER,
    {
      provide: HeaderConfig,
      useFactory: () => (config instanceof InjectionToken ? inject(config) : config),
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [HeaderConfig],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [HeaderConfig, [new Optional(), 'baseUrl']],
    },
    {
      provide: 'portalLink',
      useFactory: (headerConfig: HeaderConfig) => headerConfig.portalURL,
      deps: [HeaderConfig],
    },
    {
      provide: 'redirectLink',
      useFactory: (headerConfig: HeaderConfig) => headerConfig.redirectUri,
      deps: [HeaderConfig],
    },
    MsalInterceptor,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: GeaUiLibConfiguration,
      useFactory: (baseUrl?: string) => {
        if (!baseUrl) {
          console.warn(
            'GeaUiLibConfiguration: baseUrl is not provided, backend requests will not work',
          );
        }
        return new GeaUiLibConfiguration({
          basePath: baseUrl + 'geaid/v1',
        });
      },
      deps: [[new Optional(), 'baseUrl']],
      multi: false,
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useFactory: () => {
        const translate = inject(TranslateService);
        const resourcesService = inject(ResourcesService);
        const monitoringService = inject(MonitoringService);
        const msalBroadcastService = inject(MsalBroadcastService);
        const loginService = inject(LoginService);
        const headerConfig = inject(HeaderConfig, { optional: true });

        return () => {
          if (headerConfig) {
            resourcesService.setUrl(headerConfig.urls?.resources);
          } else {
            throw new Error(
              'provideGea: HeaderConfig is not available. ' +
                'Ensure provideGea(config) is called with a valid HeaderConfig or InjectionToken<HeaderConfig>.',
            );
          }

          msalBroadcastService.msalSubject$
            .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE))
            .subscribe((resp) => {
              if (resp?.error?.message.includes('AADB2C90091')) {
                msalConfig.auth.navigateToLoginRequestUrl = true;
                const redirectRequest: RedirectRequest = {
                  authority: msalConfig.auth.authority,
                  scopes: ['openid'],
                };
                loginService.login(redirectRequest);
              }
            });

          monitoringService.sendTrackEvent('provideGea', 'initialized');

          translate.setDefaultLang('en-US');
          translate.use(translate.currentLang || translate.defaultLang || 'en-US');
        };
      },
    },
  ];

  return makeEnvironmentProviders(providers);
}
